import { type LegacyRef,useEffect, useRef } from 'react'

import {
  buildReaderEpisodeSummaries,
  type ComicReaderEpisodeSummary,
  type ComicReaderPageEntry,
} from '../../../lib/comicReaderModel'
import { ChromePrimaryButton } from '../../ChromeActions'
import { ThemeModal } from '../../modal'
import { ComicRailDirectoryButton } from '../ComicRailSection'

type EpisodeListProps = {
  angular: boolean
  entries: ComicReaderPageEntry[]
  activeEpisodeId: string | null
  onSelectEpisode: (episodeId: string) => void
}

function readerEpisodeRowClass(angular: boolean, active: boolean) {
  return [
    'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left transition outline-none focus-visible:ring-2 focus-visible:ring-accent',
    angular ? 'rounded-none' : 'rounded-lg',
    active ? 'bg-accent font-medium text-white' : 'text-ink hover:bg-surface-muted',
  ].join(' ')
}

function ComicReaderEpisodeList({
  angular,
  entries,
  activeEpisodeId,
  onSelectEpisode,
}: EpisodeListProps) {
  const summaries = buildReaderEpisodeSummaries(entries)
  const activeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [activeEpisodeId])

  if (summaries.length === 0) {
    return <p className="px-3 py-6 text-center text-sm text-ink-soft">暂无话次</p>
  }

  return (
    <nav className="flex min-h-0 flex-col gap-1" aria-label="话次目录">
      {summaries.map((summary) => (
        <EpisodeRow
          key={summary.episodeId}
          angular={angular}
          summary={summary}
          active={summary.episodeId === activeEpisodeId}
          buttonRef={summary.episodeId === activeEpisodeId ? activeRef : undefined}
          onSelect={() => onSelectEpisode(summary.episodeId)}
        />
      ))}
    </nav>
  )
}

function EpisodeRow({
  angular,
  summary,
  active,
  buttonRef,
  onSelect,
}: {
  angular: boolean
  summary: ComicReaderEpisodeSummary
  active: boolean
  buttonRef?: LegacyRef<HTMLButtonElement>
  onSelect: () => void
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="tab"
      aria-selected={active}
      className={readerEpisodeRowClass(angular, active)}
      onClick={onSelect}
      title={summary.episodeTitle}
    >
      <span className="min-w-0 flex-1 truncate text-sm">{summary.episodeTitle}</span>
      <span
        className={[
          'shrink-0 text-xs tabular-nums',
          active ? 'text-white/80' : 'text-ink-soft',
        ].join(' ')}
      >
        {summary.pageCount} 页
      </span>
    </button>
  )
}

type SidebarProps = EpisodeListProps & {
  className?: string
}

/** PC：左侧话次目录（与编辑页话次侧栏同一结构） */
export function ComicReaderTocSidebar({ angular, className = '', ...listProps }: SidebarProps) {
  return (
    <aside
      className={[
        'shell-rail hidden h-full min-h-0 w-52 shrink-0 flex-col border-[var(--shell-rail-border)] bg-[var(--shell-rail-bg)] lg:flex xl:w-56',
        angular ? 'border-r-2' : 'border-r',
        className,
      ].join(' ')}
    >
      <div className="shrink-0 border-b border-line px-4 py-3">
        <p className="typo-lib-section text-ink">话次目录</p>
      </div>
      <div className="scroll-region min-h-0 flex-1 overflow-y-auto p-2">
        <ComicReaderEpisodeList angular={angular} {...listProps} />
      </div>
    </aside>
  )
}

type ModalProps = EpisodeListProps & {
  open: boolean
  onClose: () => void
}

/** 移动端：ThemeModal 话次目录（与 ComicEpisodeDirectoryModal 同系） */
export function ComicReaderTocModal({
  open,
  onClose,
  angular,
  onSelectEpisode,
  ...listProps
}: ModalProps) {
  const pick = (episodeId: string) => {
    onSelectEpisode(episodeId)
    onClose()
  }

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title="话次目录"
      angular={angular}
      size="md"
      bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
      footer={
        <div className="flex w-full justify-end">
          <ChromePrimaryButton density="compact" type="button" onClick={onClose}>
            完成
          </ChromePrimaryButton>
        </div>
      }
    >
      <ComicReaderEpisodeList angular={angular} {...listProps} onSelectEpisode={pick} />
    </ThemeModal>
  )
}

export function ComicReaderTocToolbarButton({
  angular,
  onOpenToc,
}: {
  angular: boolean
  onOpenToc: () => void
}) {
  return (
    <div className="shrink-0 lg:hidden">
      <ComicRailDirectoryButton
        angular={angular}
        label="话次目录"
        tooltip="选择话次"
        onClick={onOpenToc}
      />
    </div>
  )
}
